import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageShell } from '../components/Layout';
import { LogoMark, BRAND_NAME } from '../components/Logo';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { formatTimestamp } from '../utils/dateUtils';
import { QuestionHistory } from '../types';
import { getDemoSessionToken, clearDemoSessionToken } from '../utils/demoSession';
import { trackEvent } from '../utils/clarity';

// Mirrors server/src/demo/demoSessionStore.ts's MAX_DEMO_QUESTIONS/MAX_DEMO_QUESTION_CHARS
// — this is inline (client-side) validation only, for immediate feedback before a
// request is even sent. The server enforces both independently and is the real limit;
// nothing here is trusted as the actual guard.
const DEMO_MAX_QUESTIONS = 5;
const DEMO_MAX_CHARS = 500;

const SUGGESTED_QUESTIONS = [
  'What activities have the least float?',
  'Are there any trade conflicts next week?',
  'Which milestones are at risk?',
  'What is the current critical path?',
  'Show me activities finishing late.',
];

const AnswerCard: React.FC<{ answer: string }> = ({ answer }) => (
  <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4 fade-in">
    <div className="flex items-center gap-2 mb-3">
      <LogoMark variant="onLight" height={18} />
      <p className="text-xs font-semibold text-ink dark:text-gray-200 uppercase tracking-wide">
        {BRAND_NAME}
      </p>
    </div>
    <div className="ask-answer text-sm">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown>
    </div>
  </div>
);

const Ask: React.FC = () => {
  const { project_id } = useParams<{ project_id: string }>();
  const { projects, setSelectedProject } = useApp();
  const { userEmail } = useAuth();
  const flowClient = useFlowClient();

  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<QuestionHistory[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDemoMode = !!getDemoSessionToken();
  // Starts at the known server-side max; synced to the server's authoritative count
  // after every response (success or limit-reached) — never advanced optimistically.
  const [demoRemaining, setDemoRemaining] = useState(DEMO_MAX_QUESTIONS);
  const demoLimitReached = isDemoMode && demoRemaining <= 0;
  const demoTooLong = isDemoMode && question.length > DEMO_MAX_CHARS;

  const project = projects.find((p) => p.id === project_id);

  useEffect(() => {
    if (project) setSelectedProject(project);
  }, [project, setSelectedProject]);

  const handleSubmit = async () => {
    const q = question.trim();
    if (!q || loading || !userEmail || !project_id) return;
    // Inline guards — the server enforces both independently and is the real limit;
    // these just avoid a pointless round trip when the outcome is already known.
    if (demoLimitReached) return;
    if (isDemoMode && q.length > DEMO_MAX_CHARS) {
      setError(`Question is too long for the demo — please keep it under ${DEMO_MAX_CHARS} characters.`);
      return;
    }

    setLoading(true);
    setError(null);
    setCurrentAnswer(null);
    setCurrentQuestion(q);
    setQuestion('');

    try {
      const res = await flowClient.ask(userEmail, project_id, q);
      const answer = res.answer || 'No response received.';
      setCurrentAnswer(answer);
      if (isDemoMode) trackEvent('ai_question_submitted');
      setHistory((prev) => [
        { id: Date.now().toString(), question: q, answer, timestamp: new Date() },
        ...prev,
      ]);
      if (res.demo_questions_remaining !== undefined) setDemoRemaining(res.demo_questions_remaining);
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const body = (err as { body?: { demo_limit_reached?: boolean; demo_questions_remaining?: number; error?: string } } | undefined)?.body;
      if (status === 429 && body?.demo_limit_reached) {
        // The dedicated "limit reached" panel below is the only thing shown for this
        // case — no generic error banner, so nothing like "Request failed (429)" is
        // ever visible. That wrapper is useful in a dev console, not user-facing copy.
        setDemoRemaining(body.demo_questions_remaining ?? 0);
        setCurrentQuestion(null);
        setError(null);
      } else {
        // Prefer the server's own clean message (body.error) over err.message, which is
        // wrapped as "Request failed (STATUS): ..." — a developer-facing shape, not copy
        // a visitor should ever see.
        setError(body?.error ?? (err instanceof Error ? err.message : 'Failed to get an answer. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggest = (q: string) => {
    setQuestion(q);
    textareaRef.current?.focus();
  };

  return (
    <PageShell className="mt-4 space-y-4 pb-10">
        {project && (
          <div className="pt-2 pb-1">
            <h1 className="h1-page break-anywhere">{project.project_name}</h1>
            <p className="text-[15px] text-muted">Schedule Q&A</p>
          </div>
        )}
        {/* Input */}
        <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2">
            <label className="block text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide">
              {isDemoMode ? `Ask ${BRAND_NAME} AI` : 'Ask anything about your schedule'}
            </label>
            {isDemoMode && !demoLimitReached && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-tint text-accent">
                {demoRemaining} {demoRemaining === 1 ? 'question' : 'questions'} remaining
              </span>
            )}
          </div>

          {demoLimitReached ? (
            <div className="bg-crit-bg border border-crit-text rounded-[8px] p-4">
              <p className="text-sm font-medium text-crit-text mb-1">You've used all 5 demo questions.</p>
              <p className="text-xs text-crit-text mb-3">Create an account to continue using {BRAND_NAME} AI.</p>
              <button
                onClick={() => { clearDemoSessionToken(); window.location.href = '/?requestAccess=1'; }}
                className="text-xs font-semibold bg-crit-text hover:opacity-90 text-white px-4 py-2 rounded-full transition-opacity"
              >
                Create an account →
              </button>
            </div>
          ) : (
            <>
              <textarea
                ref={textareaRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. What activities are on the critical path? Are there any trade conflicts next week?"
                rows={3}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-[8px] px-3 py-2.5 bg-white dark:bg-gray-700 text-text dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              />
              {isDemoMode && (
                <p className={`text-xs mt-1 ${demoTooLong ? 'text-crit-text font-medium' : 'text-muted dark:text-gray-400'}`}>
                  {demoTooLong
                    ? `Too long for the demo — ${question.length} / ${DEMO_MAX_CHARS} characters`
                    : `${question.length} / ${DEMO_MAX_CHARS} characters`}
                </p>
              )}

              <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
                {SUGGESTED_QUESTIONS.slice(0, 3).map((q) => (
                  <button
                    key={q}
                    onClick={() => handleSuggest(q)}
                    className="inline-flex items-center text-left text-xs text-accent dark:text-green-400 bg-tint dark:bg-green-900/20 px-2.5 py-1 rounded-full hover:bg-tint transition-colors tap"
                  >
                    {q}
                  </button>
                ))}
              </div>

              <button
                onClick={handleSubmit}
                disabled={!question.trim() || loading || demoTooLong}
                className="w-full bg-accent hover:bg-link disabled:opacity-50 text-white font-semibold py-3 rounded-[8px] text-sm transition-colors min-h-[44px]"
              >
                {loading ? 'Asking…' : `Ask ${BRAND_NAME}`}
              </button>
            </>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-6 text-center fade-in">
            <div className="flex justify-center mb-3">
              <div className="w-8 h-8 border-[3px] border-accent border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-sm font-medium text-text dark:text-gray-100">
              {BRAND_NAME} is reading your schedule…
            </p>
            <p className="text-xs text-muted dark:text-gray-400 mt-1">
              Analyzing activities, float, and dependencies.
            </p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-crit-bg border border-crit-text rounded-[8px] p-4">
            <p className="text-sm font-medium text-crit-text mb-1">Could not get an answer</p>
            <p className="text-xs text-crit-text">{error}</p>
          </div>
        )}

        {/* Current Q&A */}
        {currentAnswer && !loading && (
          <div className="space-y-2">
            <div className="bg-tint rounded-[8px] px-4 py-2.5">
              <p className="text-xs text-muted dark:text-gray-400 mb-0.5">Your question</p>
              <p className="text-sm font-medium text-text dark:text-gray-100">{currentQuestion}</p>
            </div>
            <AnswerCard answer={currentAnswer} />
          </div>
        )}

        {/* Question history */}
        {history.length > 1 && (
          <div>
            <p className="text-xs font-semibold text-muted dark:text-gray-400 uppercase tracking-wide mb-2 mt-4">
              Previous questions
            </p>
            <div className="space-y-2">
              {history.slice(1).map((h) => (
                <div
                  key={h.id}
                  className="bg-white dark:bg-gray-800 rounded-[8px] border border-gray-100 dark:border-gray-700 p-3"
                >
                  <div className="flex justify-between items-start gap-2 mb-1.5">
                    <p className="text-sm font-medium text-text dark:text-gray-100 flex-1 min-w-0">
                      {h.question}
                    </p>
                    <span className="text-xs text-muted dark:text-gray-400 shrink-0">
                      {formatTimestamp(h.timestamp)}
                    </span>
                  </div>
                  <p className="text-xs text-muted dark:text-gray-400 line-clamp-2">
                    {h.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !currentAnswer && history.length === 0 && (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">💬</div>
            <p className="text-base font-semibold text-text dark:text-gray-100">
              Ask anything about your schedule
            </p>
            <p className="text-sm text-muted dark:text-gray-400 mt-1 mb-4">
              Float days, trade conflicts, milestone risk — just ask.
            </p>
            <div className="flex flex-col gap-2">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => handleSuggest(q)}
                  className="text-left text-sm text-accent dark:text-green-400 bg-tint dark:bg-green-900/20 px-4 py-2.5 rounded-[8px] hover:bg-tint transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      
    </PageShell>
  );
};

export default Ask;
